import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import * as THREE from 'three'
import { viewerApi } from '@viewer/lib/viewerApi'
import { useAppStore } from '@core/store'

// CSS-3D view cube widget. Replicates the BCF viewer pattern: 6 HTML
// faces, each subdivided in a 3×3 grid (centre = face, edges = 4
// straight neighbours, corners = 4 diagonals). The cube CSS transform
// mirrors the main camera azimuth/elevation each frame; clicks on
// cells re-orient the main camera. SVG/HTML compass cardinals project
// world directions onto the camera basis each frame.

type Vec3 = [number, number, number]
type PresetView =
  | 'top'
  | 'bottom'
  | 'front'
  | 'back'
  | 'left'
  | 'right'

interface FaceDef {
  view: PresetView
  label: string
  // CSS transform that places this face in the cube.
  transform: string
  // Local axes of the face (in world coords, since the cube's local
  // frame matches world frame here).
  right: Vec3
  up: Vec3
  normal: Vec3
}

const HALF = 36 // px, half cube edge

const FACES: FaceDef[] = [
  {
    view: 'front',
    label: 'FRENTE',
    transform: `translateZ(${HALF}px)`,
    right: [1, 0, 0],
    up: [0, 1, 0],
    normal: [0, 0, 1],
  },
  {
    view: 'back',
    label: 'TRÁS',
    transform: `rotateY(180deg) translateZ(${HALF}px)`,
    right: [-1, 0, 0],
    up: [0, 1, 0],
    normal: [0, 0, -1],
  },
  {
    view: 'right',
    label: 'DIR',
    transform: `rotateY(90deg) translateZ(${HALF}px)`,
    right: [0, 0, -1],
    up: [0, 1, 0],
    normal: [1, 0, 0],
  },
  {
    view: 'left',
    label: 'ESQ',
    transform: `rotateY(-90deg) translateZ(${HALF}px)`,
    right: [0, 0, 1],
    up: [0, 1, 0],
    normal: [-1, 0, 0],
  },
  {
    view: 'top',
    label: 'TOPO',
    transform: `rotateX(90deg) translateZ(${HALF}px)`,
    right: [1, 0, 0],
    up: [0, 0, -1],
    normal: [0, 1, 0],
  },
  {
    view: 'bottom',
    label: 'BASE',
    transform: `rotateX(-90deg) translateZ(${HALF}px)`,
    right: [1, 0, 0],
    up: [0, 0, 1],
    normal: [0, -1, 0],
  },
]

interface CompassEntry {
  label: string
  view: PresetView
  worldDir: Vec3
}

const COMPASS: CompassEntry[] = [
  { label: 'N', view: 'back', worldDir: [0, 0, -1] },
  { label: 'E', view: 'right', worldDir: [1, 0, 0] },
  { label: 'S', view: 'front', worldDir: [0, 0, 1] },
  { label: 'O', view: 'left', worldDir: [-1, 0, 0] },
]

const STAGE = 160
const STAGE_CENTER = STAGE / 2
const COMPASS_RADIUS = 60

type CellKind = 'face' | 'edge' | 'corner'

function cellKind(col: number, row: number): CellKind {
  const onCenterCol = col === 1
  const onCenterRow = row === 1
  if (onCenterCol && onCenterRow) return 'face'
  if (onCenterCol !== onCenterRow) return 'edge'
  return 'corner'
}

function cellDir(face: FaceDef, col: number, row: number): Vec3 {
  const r = col - 1 // -1, 0, +1
  const u = 1 - row // +1 top, -1 bottom (row 0 is top)
  return [
    face.normal[0] + r * face.right[0] + u * face.up[0],
    face.normal[1] + r * face.right[1] + u * face.up[1],
    face.normal[2] + r * face.right[2] + u * face.up[2],
  ]
}

/** Roda um vector em torno da vertical (Y), em graus. */
function spinY(v: Vec3, deg: number): Vec3 {
  if (!deg) return v
  const r = (deg * Math.PI) / 180
  const c = Math.cos(r)
  const s = Math.sin(r)
  return [v[0] * c + v[2] * s, v[1], -v[0] * s + v[2] * c]
}

function presetDirection(view: PresetView): Vec3 {
  switch (view) {
    case 'top':
      return [0, 1, 0]
    case 'bottom':
      return [0, -1, 0]
    case 'front':
      return [0, 0, 1]
    case 'back':
      return [0, 0, -1]
    case 'right':
      return [1, 0, 0]
    case 'left':
      return [-1, 0, 0]
  }
}

function ViewCube() {
  // Forces re-render when the camera matrix changes (rAF + fingerprint).
  const [, force] = useState(0)
  // Rotação de apresentação: roda o que Frente/Trás/Dir/Esq querem
  // dizer, para se poder olhar um edifício alinhado com o ecrã. Nunca
  // toca no modelo nem na georreferenciação, e a bússola ignora-a.
  const bearing = useAppStore((s) => s.displayBearingDeg)
  const setBearing = useAppStore((s) => s.setDisplayBearing)
  const bearingPick = useAppStore((s) => s.bearingPick)
  const setBearingPick = useAppStore((s) => s.setBearingPick)
  const fingerprintRef = useRef('')
  const cubeRef = useRef<HTMLDivElement | null>(null)
  const dragRef = useRef<{
    pointerId: number
    startX: number
    startY: number
    lastX: number
    lastY: number
    moved: boolean
  } | null>(null)

  // rAF loop: re-render the widget when camera moves.
  useEffect(() => {
    let raf = 0
    let cancelled = false
    const tick = () => {
      if (cancelled) return
      const world = viewerApi.getWorld()
      if (world) {
        const m = world.camera.three.matrixWorldInverse.elements
        const fp = `${m[0].toFixed(4)}|${m[5].toFixed(4)}|${m[12].toFixed(2)}|${m[13].toFixed(2)}|${m[14].toFixed(2)}`
        if (fp !== fingerprintRef.current) {
          fingerprintRef.current = fp
          force((t) => (t + 1) & 0xffff)
        }
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => {
      cancelled = true
      cancelAnimationFrame(raf)
    }
  }, [])

  // Read camera state.
  const world = viewerApi.getWorld()
  const camOrient = world ? readCameraOrientation(world) : null

  // Cube transform: mirror camera (negate az/el).
  const cubeTransform = camOrient
    ? `rotateX(${-camOrient.elevationDeg}deg) rotateY(${
        -camOrient.azimuthDeg + bearing
      }deg)`
    : 'rotateX(0) rotateY(0)'

  // ---- Click-to-orient ----
  /** Frente, Trás, Dir e Esq são direcções do PROJECTO: rodam com a
   *  rotação de apresentação, para se poder olhar um edifício alinhado
   *  com o ecrã. A bússola não passa por aqui, porque N, E, S e O são o
   *  mundo real e não se movem. */
  const goToPresetView = (view: PresetView) => {
    const w = viewerApi.getWorld()
    if (!w) return
    moveCameraToDirection(w, spinY(presetDirection(view), bearing))
  }
  const goToCustomDirection = (dir: Vec3) => {
    const w = viewerApi.getWorld()
    if (!w) return
    moveCameraToDirection(w, dir)
  }
  const goHome = () => {
    const w = viewerApi.getWorld()
    if (!w) return
    fitHomeView(w)
  }
  const rollCamera = (deg: number) => {
    const w = viewerApi.getWorld()
    if (!w) return
    orbitAroundVertical(w, deg)
  }

  // Drag detection: only treat as click if the cumulative move stayed
  // within 4px between pointerdown and pointerup.
  const consumeClickIfDragged = () => {
    const drag = dragRef.current
    if (drag?.moved) return true
    return false
  }

  const onStagePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      lastX: e.clientX,
      lastY: e.clientY,
      moved: false,
    }
  }
  const onStagePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag || e.pointerId !== drag.pointerId) return
    const totalDx = e.clientX - drag.startX
    const totalDy = e.clientY - drag.startY
    if (!drag.moved && Math.abs(totalDx) < 4 && Math.abs(totalDy) < 4) return
    if (!drag.moved) {
      drag.moved = true
      ;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)
    }
    const dx = e.clientX - drag.lastX
    const dy = e.clientY - drag.lastY
    drag.lastX = e.clientX
    drag.lastY = e.clientY
    const w = viewerApi.getWorld()
    if (w) orbitMainCamera(w, dx, dy)
  }
  const onStagePointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag || e.pointerId !== drag.pointerId) return
    // Keep dragRef populated until the click event fires so click
    // handlers can consume it via consumeClickIfDragged. Reset on
    // next tick.
    setTimeout(() => {
      dragRef.current = null
    }, 0)
  }

  return (
    <div
      className="pointer-events-auto absolute right-3 top-3 z-20 hidden select-none md:block"
      style={
        {
          width: STAGE,
          height: STAGE,
          perspective: '400px',
        } as CSSProperties
      }
      onPointerDown={onStagePointerDown}
      onPointerMove={onStagePointerMove}
      onPointerUp={onStagePointerUp}
      onPointerCancel={onStagePointerUp}
    >
      {/* Home button (top-left) */}
      <button
        type="button"
        onClick={() => {
          if (consumeClickIfDragged()) return
          goHome()
        }}
        title="Vista inicial (enquadrar modelo)"
        className="absolute left-0 top-0 z-30 flex h-5 w-5 items-center justify-center rounded border border-[var(--color-accent)] bg-[var(--color-surface)]/80 text-[var(--color-accent)] hover:bg-[var(--color-accent)] hover:text-[var(--color-bg)]"
      >
        <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden>
          <path
            d="M2 6 L6 2 L10 6 M3 5.5 V10 H9 V5.5"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.4}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Roll buttons (top-right) */}
      <div className="absolute right-0 top-0 z-30 flex gap-0.5">
        <button
          type="button"
          onClick={() => {
            if (consumeClickIfDragged()) return
            rollCamera(90)
          }}
          title="Orbitar -90° à volta da vertical (anti-horário)"
          className="flex h-5 w-5 items-center justify-center rounded border border-[var(--color-accent)] bg-[var(--color-surface)]/80 text-[var(--color-accent)] hover:bg-[var(--color-accent)] hover:text-[var(--color-bg)]"
        >
          <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden>
            <path
              d="M3 8 A4 4 0 1 1 8 3 M3 5 V8 H6"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.4}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => {
            if (consumeClickIfDragged()) return
            rollCamera(-90)
          }}
          title="Orbitar 90° à volta da vertical (horário)"
          className="flex h-5 w-5 items-center justify-center rounded border border-[var(--color-accent)] bg-[var(--color-surface)]/80 text-[var(--color-accent)] hover:bg-[var(--color-accent)] hover:text-[var(--color-bg)]"
        >
          <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden>
            <path
              d="M9 8 A4 4 0 1 0 4 3 M9 5 V8 H6"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.4}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        {/* Rotação de apresentação: muda o que as vistas padrão querem
            dizer, e fica. Distinta do orbitar acima, que só move a
            câmara agora. */}
        <button
          type="button"
          onClick={() => {
            if (consumeClickIfDragged()) return
            setBearing(bearing + 90)
          }}
          title="Rodar a apresentação 90°: muda o que Frente, Trás, Dir e Esq querem dizer, para alinhares o edifício com o ecrã. Não toca no modelo, na georreferenciação nem no norte verdadeiro, e não desalinha modelos federados."
          className={`flex h-5 items-center justify-center rounded border px-1 text-[9px] font-semibold ${
            bearing
              ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-[var(--color-bg)]'
              : 'border-[var(--color-accent)] bg-[var(--color-surface)]/80 text-[var(--color-accent)] hover:bg-[var(--color-accent)] hover:text-[var(--color-bg)]'
          }`}
        >
          {bearing ? `${Math.round(bearing)}°` : 'N⊾'}
        </button>
        {/* Traçar a linha que fica horizontal: dois cliques sobre o
            modelo. É a forma precisa de alinhar, contra os saltos de
            90° do botão acima. */}
        <button
          type="button"
          onClick={() => {
            if (consumeClickIfDragged()) return
            if (!bearingPick) useAppStore.getState().clearSelection()
            setBearingPick(bearingPick ? null : [])
          }}
          title="Clica numa fachada e a vista roda para ela ficar horizontal, com a face do cubo alinhada. Um clique só. Não toca no modelo nem na georreferenciação."
          className={`flex h-5 items-center justify-center rounded border px-1 text-[9px] font-semibold ${
            bearingPick
              ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-[var(--color-bg)]'
              : 'border-[var(--color-accent)] bg-[var(--color-surface)]/80 text-[var(--color-accent)] hover:bg-[var(--color-accent)] hover:text-[var(--color-bg)]'
          }`}
        >
          {bearingPick ? 'clica' : '∠'}
        </button>
        {bearing !== 0 && (
          <button
            type="button"
            onClick={() => {
              if (consumeClickIfDragged()) return
              setBearing(0)
            }}
            title="Voltar à orientação original do modelo."
            className="flex h-5 w-5 items-center justify-center rounded border border-[var(--color-border)] bg-[var(--color-surface)]/80 text-[10px] text-[var(--color-text-muted)] hover:border-[var(--color-accent)] hover:text-[var(--color-accent)]"
          >
            ×
          </button>
        )}
      </div>

      {/* Cube container */}
      <div
        ref={cubeRef}
        className="absolute left-1/2 top-1/2"
        style={{
          width: HALF * 2,
          height: HALF * 2,
          marginLeft: -HALF,
          marginTop: -HALF,
          transformStyle: 'preserve-3d',
          transform: cubeTransform,
        }}
      >
        {FACES.map((face) => (
          <div
            key={face.view}
            className="absolute"
            style={{
              width: HALF * 2,
              height: HALF * 2,
              transform: face.transform,
              backfaceVisibility: 'hidden',
            }}
          >
            <div className="grid h-full w-full grid-cols-3 grid-rows-3 border border-[var(--color-accent)]/60 bg-[var(--color-surface)]/85">
              {Array.from({ length: 9 }).map((_, idx) => {
                const col = idx % 3
                const row = Math.floor(idx / 3)
                const kind = cellKind(col, row)
                const onClick = () => {
                  if (consumeClickIfDragged()) return
                  if (kind === 'face') goToPresetView(face.view)
                  else goToCustomDirection(cellDir(face, col, row))
                }
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={onClick}
                    className={`flex items-center justify-center text-[8px] uppercase transition-colors ${
                      kind === 'face'
                        ? 'border border-[var(--color-accent)]/30 text-[var(--color-text)] hover:bg-[var(--color-accent-bg)]'
                        : kind === 'edge'
                          ? 'border border-[var(--color-accent)]/15 text-[var(--color-text-muted)] hover:bg-[var(--color-accent-bg)]'
                          : 'border border-[var(--color-accent)]/10 text-[var(--color-text-dim)] hover:bg-[var(--color-accent-bg)]'
                    }`}
                  >
                    {kind === 'face' ? face.label : ''}
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Compass cardinals */}
      {camOrient &&
        COMPASS.map((c) => {
          const screen = projectCardinal(camOrient, c.worldDir)
          const visible = screen !== null
          return (
            <button
              key={c.label}
              type="button"
              onClick={() => {
                if (consumeClickIfDragged()) return
                // Pelo MUNDO, não pela vista padrão: carregar no N tem
                // de levar ao norte verdadeiro, rode-se o que se rodar.
                goToCustomDirection(c.worldDir)
              }}
              title={`${c.label}, vista ${c.view}`}
              className="absolute z-30 flex h-5 w-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-[var(--color-accent)] bg-[var(--color-surface)]/85 text-[10px] font-semibold text-[var(--color-accent)] hover:bg-[var(--color-accent)] hover:text-[var(--color-bg)]"
              style={{
                left: screen
                  ? STAGE_CENTER + screen.x * COMPASS_RADIUS
                  : STAGE_CENTER,
                top: screen
                  ? STAGE_CENTER - screen.y * COMPASS_RADIUS
                  : STAGE_CENTER,
                opacity: visible ? 1 : 0,
                pointerEvents: visible ? 'auto' : 'none',
                transition: 'opacity 120ms',
              }}
            >
              {c.label}
            </button>
          )
        })}
    </div>
  )
}

// ---- Camera helpers ----

interface CameraOrient {
  azimuthDeg: number
  elevationDeg: number
  // Camera basis vectors (forward, right, up) in world coords. Used
  // by compass projection.
  forward: THREE.Vector3
  right: THREE.Vector3
  up: THREE.Vector3
}

function readCameraOrientation(
  world: NonNullable<ReturnType<typeof viewerApi.getWorld>>,
): CameraOrient {
  const cam = world.camera.three
  const tgt = new THREE.Vector3()
  world.camera.controls.getTarget(tgt)
  const forward = new THREE.Vector3()
    .subVectors(tgt, cam.position)
    .normalize()
  // The cube's local frame matches THREE's world (Y up, looking -Z is
  // "front"). Use atan2(-fx, -fz) so that forward = (0,0,-1) gives
  // azimuth 0; and asin(-fy) so that forward = (0,-1,0) (looking
  // down) gives elevation +90.
  const azimuth = Math.atan2(-forward.x, -forward.z)
  const elevation = Math.asin(THREE.MathUtils.clamp(-forward.y, -1, 1))
  const up = cam.up.clone().normalize()
  const right = new THREE.Vector3().crossVectors(forward, up).normalize()
  const upTrue = new THREE.Vector3().crossVectors(right, forward).normalize()
  return {
    azimuthDeg: (azimuth * 180) / Math.PI,
    elevationDeg: (elevation * 180) / Math.PI,
    forward,
    right,
    up: upTrue,
  }
}

function projectCardinal(
  orient: CameraOrient,
  dir: Vec3,
): { x: number; y: number } | null {
  const v = new THREE.Vector3(dir[0], dir[1], dir[2])
  const sx = orient.right.dot(v)
  const sy = orient.up.dot(v)
  const len = Math.hypot(sx, sy)
  if (len < 0.05) return null
  return { x: sx / len, y: sy / len }
}

function moveCameraToDirection(
  world: NonNullable<ReturnType<typeof viewerApi.getWorld>>,
  dir: Vec3,
) {
  const cam = world.camera.three
  const cc = world.camera.controls
  const tgt = new THREE.Vector3()
  cc.getTarget(tgt)
  const distance = cam.position.distanceTo(tgt) || 50
  const v = new THREE.Vector3(dir[0], dir[1], dir[2])
  if (v.lengthSq() < 1e-6) return
  v.normalize()
  const newPos = tgt.clone().addScaledVector(v, distance)
  // Avoid up-vector collinear with view direction: when looking
  // straight up/down, fall back to a Z-aligned up.
  const dotUp = Math.abs(v.dot(new THREE.Vector3(0, 1, 0)))
  if (dotUp > 0.99) {
    cam.up.set(0, 0, v.y > 0 ? -1 : 1)
  } else {
    cam.up.set(0, 1, 0)
  }
  void cc.setLookAt(
    newPos.x,
    newPos.y,
    newPos.z,
    tgt.x,
    tgt.y,
    tgt.z,
    true,
  )
}

function fitHomeView(
  world: NonNullable<ReturnType<typeof viewerApi.getWorld>>,
) {
  const box = new THREE.Box3()
  let initialised = false
  for (const m of world.scene.three.children) {
    if (m.name && /\.ifc$/i.test(m.name)) {
      const mb = new THREE.Box3().setFromObject(m)
      if (!mb.isEmpty()) {
        if (!initialised) {
          box.copy(mb)
          initialised = true
        } else box.union(mb)
      }
    }
  }
  if (!initialised) return
  const center = new THREE.Vector3()
  box.getCenter(center)
  const size = new THREE.Vector3()
  box.getSize(size)
  const maxDim = Math.max(size.x, size.y, size.z) || 10
  const d = maxDim * 1.8
  world.camera.three.up.set(0, 1, 0)
  void world.camera.controls.setLookAt(
    center.x + d,
    center.y + d,
    center.z + d,
    center.x,
    center.y,
    center.z,
    true,
  )
}

function orbitMainCamera(
  world: NonNullable<ReturnType<typeof viewerApi.getWorld>>,
  dx: number,
  dy: number,
) {
  // camera-controls.rotate: deltas are in radians. ~0.5° per pixel
  // matches the BCF viewer's feel.
  const SENS = (Math.PI / 180) * 0.5
  void world.camera.controls.rotate(dx * SENS, dy * SENS, true)
}

function orbitAroundVertical(
  world: NonNullable<ReturnType<typeof viewerApi.getWorld>>,
  deg: number,
) {
  // Turntable rotation around the cube's vertical (world Y / cached
  // _yAxisUpSpace). Cube stays upright; model spins underneath.
  //
  // Use cc.rotate (incremental azimuth delta) instead of computing a
  // new absolute position and calling setLookAt(): setLookAt with
  // smooth=true interpolates spherical coords absolutely, which after
  // a couple of clicks crosses the azimuth wrap-around boundary and
  // animates the long way (the "360°" effect the user saw). rotate()
  // applies the delta on top of the current animated state, so each
  // click visibly steps by exactly `deg`.
  //
  // Sign: camera-controls' rotate uses positive azimuth = CCW viewed
  // from +Y, but the on-screen feel of "rodar horário" expects the
  // cube to spin clockwise on screen, which means the camera must go
  // counter-clockwise around the model. Negate so the CW button truly
  // turns the cube CW from the user's perspective.
  const cc = world.camera.controls
  void cc.rotate((-deg * Math.PI) / 180, 0, true)
}

export default ViewCube
